/**
 * Escape closes the dialog on top — including `persistent` ones.
 *
 * Vuetify's VDialog already closes on Esc, but only when the dialog is not
 * `persistent`; a persistent one just shakes (`animateClick`) and stays put.
 * 80 of this app's dialogs are persistent, so for almost every popup in the
 * product the key did nothing and the only way out was to find the × or Cancel.
 *
 * `persistent` is there to stop a stray click on the overlay throwing away a
 * half-filled form, and that is still worth having — a misplaced click is an
 * accident. Pressing Escape is not: it is a deliberate "close this". So the
 * overlay stays undismissable and the key gets its normal meaning back.
 *
 * WHY A GLOBAL MIXIN AND NOT A PATCH ON VDialog.options.methods:
 * the obvious version — import VDialog from "vuetify/lib" and overwrite
 * `options.methods.onKeydown` — builds and ships without complaint and does
 * nothing. Under vuetify-loader the component the templates instantiate is not
 * reliably the same module object you get by importing it yourself, so the
 * patch lands on a VDialog nobody renders. Hooking the instance is immune to
 * that: `initMethods` puts a bound `onKeydown` on every instance as an own
 * property, `created` runs before the first render, and VDialog reads
 * `this.onKeydown` when it renders its content — so replacing it there is what
 * the dialog actually uses.
 *
 *   - Only PERSISTENT dialogs are touched. Everything else keeps Vuetify's own
 *     Esc handling, which is already correct.
 *   - Only the TOPMOST dialog closes. `getOpenDependents()` is Vuetify's own
 *     list of overlays opened from inside this one (a nested dialog, an open
 *     v-menu or v-select). While any of those are up, Esc belongs to them —
 *     closing a select should not also close the form it sits in.
 *   - A dialog that genuinely must not be escapable opts out with the
 *     `esc-lock` attribute: `<v-dialog persistent esc-lock>`.
 *   - `keydown` is still emitted, so any component listening for it is
 *     unaffected.
 *
 * v-menu, v-select, v-autocomplete and v-combobox already close on Esc without
 * help; nothing here changes them.
 */
import Vue from "vue";

const ESC = 27;

export default function installEscapeToClose() {
  Vue.mixin({
    created() {
      if (!this.$options || this.$options.name !== "v-dialog") return;
      if (typeof this.onKeydown !== "function") return;

      const vuetifyOnKeydown = this.onKeydown;

      this.onKeydown = (e) => {
        const isEsc = e.keyCode === ESC || e.key === "Escape";
        const dependents = this.getOpenDependents
          ? this.getOpenDependents()
          : [];

        if (
          isEsc &&
          this.persistent &&
          !dependents.length &&
          !("esc-lock" in (this.$attrs || {}))
        ) {
          this.isActive = false;
          // What Vuetify does for the non-persistent case: hand focus back to
          // whatever opened the dialog, so the tab order picks up where it
          // left off.
          const activator = this.getActivator && this.getActivator();
          this.$nextTick(() => activator && activator.focus());
          this.$emit("keydown", e);
          return;
        }

        vuetifyOnKeydown(e);
      };
    },
  });
}
